import { Filter, SegmentRuleField } from "sharedTypes"

// Maps each sidebar filter to its chip equivalent so the chip row, title,
// dirty state, and Save flow can treat default sidebar picks identically to
// a custom segment. Two kinds of pick:
//
//   1. Virtual groups (Confirmed, Checked In, …) — string-id presets that
//      mirror `app/models/event_group.rb:VIRTUAL_GROUPS` and the matching
//      `SegmentViewer.filter_by_*` branches. Same scope either way.
//   2. Event groups (user-created Guest Lists like VIP, Sponsor) — numeric
//      ids that map to a single Guest List chip with the group's id as the
//      value.
//
// `unassigned` is intentionally absent from PRESETS: it's a placeholder row
// matcher (`lower(first_name) = 'unassigned'`) with no chip representation.
// It keeps its legacy `group_id=` axis and doesn't participate in chip flows.
const PRESETS: Record<string, Omit<Filter, "id">[]> = {
  confirmed: [
    { field: SegmentRuleField.Confirmation, operator: "eq", values: ["Confirmed"] },
  ],
  unconfirmed: [
    { field: SegmentRuleField.Confirmation, operator: "eq", values: ["Unconfirmed"] },
  ],
  waitlisted: [
    { field: SegmentRuleField.Confirmation, operator: "eq", values: ["Waitlisted"] },
  ],
  declined: [
    { field: SegmentRuleField.Confirmation, operator: "eq", values: ["Declined"] },
  ],
  arrived: [
    { field: SegmentRuleField.CheckInStatus, operator: "eq", values: ["Arrived"] },
  ],
  unarrived: [
    { field: SegmentRuleField.CheckInStatus, operator: "eq", values: ["Unarrived"] },
  ],
  seated: [
    { field: SegmentRuleField.Seating, operator: "eq", values: ["Assigned"] },
  ],
  unseated: [
    { field: SegmentRuleField.Seating, operator: "ne", values: ["Assigned"] },
  ],
  bounced: [
    { field: SegmentRuleField.Email, operator: "in", values: ["Hard Bounced", "Soft Bounced"] },
  ],
}

// Returns the chip representation of a sidebar group pick, or null when the
// pick has no chip representation (Unassigned Registration).
//   - Known virtual group → its preset chips.
//   - Numeric id → a Guest List chip with that id as the value (event groups
//     created by the user).
const getDefaultGroupChips = (
  groupId: string | undefined | null,
): Filter[] | null => {
  if (!groupId) return null
  const preset = PRESETS[groupId]
  if (preset) {
    return preset.map((p, i) => ({
      id: `default-${groupId}-${i}`,
      ...p,
    }))
  }
  if (/^\d+$/.test(groupId)) {
    return [{
      id: `default-${groupId}`,
      field: SegmentRuleField.GuestList,
      operator: "eq",
      values: [groupId],
    }]
  }

  return null
}

export default getDefaultGroupChips
